import { path, pipe, propEq, find, has } from 'ramda';
import React from 'react';
import styled from 'styled-components';

import { Icon } from '@ui/icons/Icon.tsx';
import { getOpenTimeLabel } from '@utils/operationHours.ts';

import { useCSSColors } from '../../../../../src/hooks/useCSSColors.ts';

const InfosList = styled.ul`
  list-style: none;
  margin-block: 3px 12px;
  margin-inline: 47px 0;
  ${({ theme }) => theme.fontSize('14px')};
  color: var(--color-secondaryText);
  padding-inline-start: 0;

  li {
    > :first-child {
      margin-inline-end: 5px;
    }
  }
`;

const IconWrapper = styled.div`
  display: inline-block;
  width: 24px;
  height: 24px;
`;

const PoiFeatures = ({ poi, T, venueCategory, structures, venueTimezone, openStatus }) => {
  const colors = useCSSColors();
  if (!poi) {
    return null;
  }

  const Feature = ({ iconId, children }) => (
    <li>
      <IconWrapper>
        <Icon className="icon" width={24} height={24} name={iconId} cdn fillColor={colors.secondaryText} />
      </IconWrapper>
      {children}
    </li>
  );

  // `showBuildingLevel: false` comes from the venue's POI data (set per POI in VMS); only an explicit false hides the rows
  const DefaultInfo = () =>
    poi.showBuildingLevel === false ? null : (
      <React.Fragment>
        <Feature iconId="levels">{getFloorDetails(poi, structures)}</Feature>
        <Feature iconId="building">{poi.position.structureName}</Feature>
      </React.Fragment>
    );

  const RoomIdInfo = () => {
    const roomInfoLabel = getRoomInfoLabel(poi);
    return !!roomInfoLabel && <Feature iconId="location">{roomInfoLabel}</Feature>;
  };

  const getRoomInfoLabel = poi => {
    if (poi.roomId) {
      const nearbyLandmark = poi.nearbyLandmark ? T('poiView:near _location_', { location: poi.nearbyLandmark }) : '';
      return joinStr([poi.roomId, nearbyLandmark], ' ');
    } else if (poi.nearbyLandmark) {
      return T('poiView:Near _location_', { location: poi.nearbyLandmark });
    }
  };

  const getFloorDetails = (poi, structures) => {
    const { floorName, floorId, buildingId } = poi.position;
    const floorDetails = pipe(find(propEq(buildingId, 'id')), path(['levels', floorId, 'details']))(structures);
    return joinStr([floorName, floorDetails]);
  };

  const MeetingInfo = () => (
    <React.Fragment>
      <DefaultInfo />
      {poi.capacity && (
        <Feature iconId="number-of-seats">
          {T('poiView:Seats')}: {poi.capacity.join('-')}
        </Feature>
      )}
    </React.Fragment>
  );

  const ParkingInfo = () => {
    // todo maybe change dynamicData to dynamicIntegration
    const { rateHour, rateDay } = path(['dynamicData', 'parking'], poi) || {};
    const getRate = rate => {
      if (!rate) {
        return '-';
      }
      return rate.match('[-]') ? rate : rate.split(' ')[0]; // if the rate returned was just in '$ -' format return it, otherwise take currency and value
    };

    if (!rateHour && !rateDay) {
      return <DefaultInfo />;
    }

    const rateForHour = getRate(rateHour);
    const rateForDay = getRate(rateDay);
    return (
      <React.Fragment>
        <DefaultInfo />
        <Feature iconId="coins">
          {T('poiView:_rate_ per hour', { rate: rateForHour })} / {T('poiView:_rate_ per day', { rate: rateForDay })}
        </Feature>
      </React.Fragment>
    );
  };

  const SecurityInfo = () => {
    const nearbyLandmark = poi.nearbyLandmark && T('poiView:Near _location_', { location: poi.nearbyLandmark });
    const isAfterSecurity = getAfterSecurityLabel();
    const securityInfo = joinStr([nearbyLandmark, isAfterSecurity]);
    return !!securityInfo && <Feature iconId="location">{securityInfo}</Feature>;
  };

  const joinStr = (args, delimiter = ' • ') => args.filter(i => i).join(delimiter);

  const getAfterSecurityLabel = () => {
    if (isSecurityCheckpoint(poi) || isParking(poi)) {
      return '';
    } // do not show this info for security checkpoint pois or parking
    if (!has('isAfterSecurity', poi)) {
      return '';
    }
    return poi.isAfterSecurity ? T('poiView:After Security') : T('poiView:Outside Security');
  };

  const OpenTimeInfo = () => {
    if (!isSecurityCheckpoint(poi)) {
      return null;
    }

    // When a status was resolved, the openClosedStatus section already displays that label,
    // so rendering it here too would show it twice. This row then only serves as the
    // fallback for when the feature is off or the status could not be determined.
    if (openStatus) {
      return null;
    }

    const label = getOpenTimeLabel(poi.operationHours, new Date(), T, venueTimezone);

    if (!label) {
      return null;
    }

    return <Feature iconId="opening-hours">{label}</Feature>;
  };

  const isSecurityCheckpoint = ({ category }) => category === 'security.checkpoint' || category === 'security';
  const isParking = ({ category }) => category.startsWith('parking');

  const getFeatures = category => {
    if (category.startsWith('meeting')) {
      return <MeetingInfo />;
    }
    if (isParking({ category })) {
      return <ParkingInfo />;
    }
    return <DefaultInfo />;
  };

  const getCasinoNearLocationLabel = () => {
    if (venueCategory === 'casino' && poi.nearbyLandmark) {
      return <Feature iconId="location">{T('poiView:Near _location_', { location: poi.nearbyLandmark })}</Feature>;
    }
  };

  return (
    <InfosList>
      {venueCategory === 'airport' && <SecurityInfo />}
      {venueCategory === 'smartcampus' && <RoomIdInfo />}
      {getCasinoNearLocationLabel()}
      {getFeatures(poi.category)}
      <OpenTimeInfo />
    </InfosList>
  );
};

export default PoiFeatures;
